import React from "react";

interface DataTableSkeletonProps {
  /** Number of placeholder body rows to render. */
  rows?: number;
  /** Number of columns to render. */
  columns?: number;
  /** Column indexes that represent icon/action buttons (rendered as a square). */
  iconColumnIndexes?: number[];
  /** Whether to render the header row. */
  showHeader?: boolean;
}

const wrapperStyle: React.CSSProperties = {
  border: "1px solid #E6E1DB",
  borderRadius: "4px",
  backgroundColor: "#FFFFFF",
};

const headerCellStyle: React.CSSProperties = {
  backgroundColor: "#F9FAFB",
  borderBottom: "1px solid #E4E3E1",
};

const bodyCellStyle: React.CSSProperties = {
  borderBottom: "1px solid #F2F1EF",
};

// Stable, slightly varied widths so the skeleton reads as content rather than a grid.
const BAR_WIDTHS = ["80%", "55%", "70%", "50%", "65%", "60%", "75%"];

/**
 * A loading placeholder that mirrors the visual structure of {@link DataTable}.
 * Keeps the layout from shifting when real rows arrive and gives a better
 * perceived performance than a spinner.
 */
const DataTableSkeleton: React.FC<DataTableSkeletonProps> = ({
  rows = 8,
  columns = 6,
  iconColumnIndexes = [],
  showHeader = true,
}) => {
  const iconColumns = new Set(iconColumnIndexes);

  return (
    <div
      className="bg-white rounded-sm border border-[#E6E1DB] overflow-hidden animate-pulse"
      style={wrapperStyle}
      aria-hidden="true"
    >
      <table className="w-full border-separate border-spacing-0">
        {showHeader && (
          <thead>
            <tr>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <th
                  key={`head-${colIndex}`}
                  className="px-4 py-3 text-left"
                  style={headerCellStyle}
                >
                  <div
                    className="h-3 rounded bg-gray-300"
                    style={{ width: iconColumns.has(colIndex) ? "24px" : "50%" }}
                  />
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr
              key={`row-${rowIndex}`}
              style={{
                backgroundColor:
                  rowIndex % 2 === 0 ? "#FFFFFF" : "rgba(249, 250, 251, 0.7)",
              }}
            >
              {Array.from({ length: columns }).map((_, colIndex) => (
                <td
                  key={`cell-${rowIndex}-${colIndex}`}
                  className="px-4 py-3"
                  style={bodyCellStyle}
                >
                  {iconColumns.has(colIndex) ? (
                    <div className="h-7 w-7 rounded bg-gray-200" />
                  ) : (
                    <div
                      className="h-3 rounded bg-gray-200"
                      style={{
                        width:
                          BAR_WIDTHS[(rowIndex + colIndex) % BAR_WIDTHS.length],
                      }}
                    />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DataTableSkeleton;
